<template>
  <div class="copy-summary-collapses">
    <div class="summary-header">
      <b-icon icon="info-circle" class="info-icon" />
      <span class="summary-title">{{ $t('resumodacopia') }}</span>
    </div>
    <p class="summary-description">{{ $t('visualizacaorapida') }}</p>

    <div class="collapsible-sections">
      <CopyCategoryCollapse
        variant="created"
        :header-text="getCreatedHeaderText()"
        :items="createdItems"
        :show="showCreated"
        @toggle="toggleCreated"
      >
        <template #item="{ item }">
          <div class="detail-item-header">
            <b-icon icon="check-circle-fill" class="item-icon success" />
            <span class="item-name">{{ getItemName(item) }}</span>
          </div>
          <div class="detail-item-subtitle">{{ getItemReason(item) }}</div>
        </template>
      </CopyCategoryCollapse>

      <CopyCategoryCollapse
        variant="substituted"
        :header-text="getSubstitutedHeaderText()"
        :items="substitutedItems"
        :show="showSubstituted"
        @toggle="toggleSubstituted"
      >
        <template #item="{ item }">
          <div class="detail-item-header">
            <b-icon icon="check-circle-fill" class="item-icon success" />
            <span class="item-name">{{ getItemName(item) }}</span>
          </div>
          <div class="detail-item-subtitle">
            {{ getItemReason(item) }}
          </div>

          <div v-if="item.hasNameVariation" class="detail-item-info">
            <b-icon icon="info-circle-fill" class="item-icon info" />
            <span class="info-text">
              {{ $t('nomenormalizado') }}: "{{ item.normalizedName }}"
            </span>
          </div>
        </template>
      </CopyCategoryCollapse>

      <CopyCategoryCollapse
        variant="ignored"
        :header-text="getIgnoredHeaderText()"
        :items="ignoredItems"
        :show="showIgnored"
        @toggle="toggleIgnored"
      >
        <template #item="{ item }">
          <div class="detail-item-header">
            <b-icon icon="x-circle-fill" class="item-icon danger" />
            <span class="item-name">{{
              $t('copysummarylabel', { label: getIgnoredItemName(item) })
            }}</span>
          </div>
          <div class="detail-item-subtitle">
            {{ getIgnoredItemSubtitle(item) }}
          </div>
        </template>
      </CopyCategoryCollapse>

      <CopyCategoryCollapse
        variant="deleted"
        :header-text="getDeletedHeaderText()"
        :items="deletedItems"
        :show="showDeleted"
        :tooltip="$t('copysummarydeletedtooltip')"
        @toggle="toggleDeleted"
      >
        <template #item="{ item }">
          <div class="detail-item-header">
            <b-icon icon="trash-fill" class="item-icon danger" />
            <span class="item-name">{{
              $t('copysummarylabel', { label: getDeletedItemName(item) })
            }}</span>
          </div>
          <div class="detail-item-subtitle">
            {{ getDeletedItemSubtitle(item) }}
          </div>
        </template>
      </CopyCategoryCollapse>
    </div>
  </div>
</template>

<script>
import CopyCategoryCollapse from './CopyCategoryCollapse.vue'

export default {
  name: 'CopySummaryCollapses',

  components: {
    CopyCategoryCollapse
  },

  props: {
    createdCount: {
      type: Number,
      default: 0
    },
    substitutedCount: {
      type: Number,
      default: 0
    },
    ignoredCount: {
      type: Number,
      default: 0
    },
    deletedCount: {
      type: Number,
      default: 0
    },
    createdItems: {
      type: Array,
      default: () => []
    },
    substitutedItems: {
      type: Array,
      default: () => []
    },
    ignoredItems: {
      type: Array,
      default: () => []
    },
    deletedItems: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      showCreated: false,
      showSubstituted: false,
      showIgnored: false,
      showDeleted: false
    }
  },

  methods: {
    getCreatedHeaderText() {
      const countLabel = this.$t(
        this.createdCount === 1 ? 'item' : 'itens'
      )
      return `${this.$t('criacoes')} (${this.createdCount} ${countLabel})`
    },

    getSubstitutedHeaderText() {
      const countLabel = this.$t(
        this.substitutedCount === 1 ? 'item' : 'itens'
      )
      return `${this.$t('substituicoes')} (${this.substitutedCount} ${countLabel})`
    },

    getIgnoredHeaderText() {
      const countLabel = this.$t(
        this.ignoredCount === 1 ? 'labelduplicado' : 'labelsduplicados'
      )
      return `${this.$t('ignorados')} (${this.ignoredCount} ${countLabel})`
    },

    getDeletedHeaderText() {
      const countLabel = this.$t(
        this.deletedCount === 1 ? 'labelremovido' : 'labelsremovidos'
      )
      return `${this.$t('deletados')} (${this.deletedCount} ${countLabel})`
    },

    toggleCreated() {
      this.showCreated = !this.showCreated
    },

    toggleSubstituted() {
      this.showSubstituted = !this.showSubstituted
    },

    toggleIgnored() {
      this.showIgnored = !this.showIgnored
    },

    toggleDeleted() {
      this.showDeleted = !this.showDeleted
    },

    getItemName(item) {
      return item.ignoredLabel || item.positionName || item.name || item.label || item
    },

    getIgnoredItemName(item) {
      return (
        item.ignoredLabel ||
        item.positionName ||
        item.name ||
        item.label ||
        item
      )
    },

    buildScopedSubtitle(item, fallbackReasonKey) {
      if (item.reasonKey || item.reason) {
        return this.getItemReason(item)
      }
      const scope = this.getIgnoredTypeLabel(item.ignoredType || item.category)
      const position = item.positionName || item.ignoredInPosition || '-'
      return this.$t(fallbackReasonKey, {
        scope,
        position
      })
    },

    getIgnoredItemSubtitle(item) {
      return this.buildScopedSubtitle(
        item,
        'copysummaryreasonalreadyexistsinposition'
      )
    },

    getDeletedItemName(item) {
      return this.getIgnoredItemName(item)
    },

    getDeletedItemSubtitle(item) {
      return this.buildScopedSubtitle(
        item,
        'copysummaryreasondeletedonlyindestination'
      )
    },

    getIgnoredTypeLabel(type) {
      const typeToKeyMap = {
        skill: 'copysummaryscopeskill',
        profile: 'copysummaryscopeprofile',
        experience: 'copysummaryscopeexperience',
        positionRole: 'copysummaryscopepositionrole',
        other: 'copysummaryscopeother'
      }
      if (!type) {
        return this.$t('copysummaryscopeother')
      }
      const translationKey = typeToKeyMap[type]
      if (translationKey) {
        return this.$t(translationKey)
      }
      return type
    },

    getItemReason(item) {
      if (item.reasonKey) {
        return this.$t(item.reasonKey, this.getReasonParams(item))
      }

      if (item.action === 'created') {
        return this.$t('copysummaryreasonnewposition')
      }

      if (item.action === 'substituted') {
        if (item.hasNameVariation) {
          return this.$t('copysummaryreasonnamevariation', {
            destination: item.destinationPositionName || '-',
            source: item.positionName || '-'
          })
        }
        return this.$t('copysummaryreasonduplicatereplaced')
      }

      if (item.action === 'ignored') {
        return this.$t('copysummaryreasonalreadyexistsinposition', {
          scope: this.getIgnoredTypeLabel(item.ignoredType),
          position: item.positionName || item.ignoredInPosition || '-'
        })
      }

      if (item.reason) {
        return item.reason
      }

      return ''
    },

    getReasonParams(item) {
      const params = {
        ...(item.reasonParams || {})
      }

      const ignoredType = params.ignoredType || item.ignoredType

      if (!params.scope && ignoredType) {
        params.scope = this.getIgnoredTypeLabel(ignoredType)
      }
      if (!params.position) {
        params.position = item.positionName || item.ignoredInPosition || '-'
      }
      if (!params.destination && item.destinationPositionName) {
        params.destination = item.destinationPositionName
      }
      if (!params.source && item.positionName) {
        params.source = item.positionName
      }

      return params
    }
  }
}
</script>

<style scoped>
.copy-summary-collapses {
  width: 100%;
  text-align: left;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.info-icon {
  color: #6c9ec4;
  font-size: 1.1rem;
}

.summary-title {
  font-weight: 600;
  color: #333;
}

.summary-description {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  margin-left: 26px;
}

.collapsible-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.detail-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-icon {
  font-size: 0.95rem;
}

.item-icon.success {
  color: #28a745;
}

.item-icon.info {
  color: #17a2b8;
}

.item-icon.danger {
  color: #dc3545;
}

.item-name {
  font-weight: 600;
  color: #333;
}

.detail-item-subtitle {
  color: #666;
  font-size: 0.85rem;
  margin-left: 23px;
  margin-top: 2px;
}

.detail-item-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 23px;
  margin-top: 4px;
}

.info-text {
  color: #17a2b8;
  font-size: 0.85rem;
}
</style>
